import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, Button, Input, Label, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getFooterAdBanners, addFooterAdBanner, deleteFooterAdBanner, updateFooterAdBanner } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const FooterAdBanner = () => {
  document.title = "Footer Ad - Dashboard";
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await getFooterAdBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to load footer ad banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const toggle = () => {
    setModal(!modal);
    setEditingBanner(null);
    setLinkUrl("");
    setSortOrder(0);
    setImageFile(null);
    setTitle("");
    setDescription("");
  };

  const handleEditClick = (banner) => {
    setEditingBanner(banner);
    setLinkUrl(banner.link_url || "");
    setSortOrder(banner.sort_order || 0);
    setImageFile(null);
    setTitle(banner.title || "");
    setDescription(banner.description || "");
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingBanner && !imageFile) {
      toast.error("Please select an image");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append("footerAdImage", imageFile);
      }
      formData.append("link_url", linkUrl);
      formData.append("sort_order", sortOrder);
      formData.append("title", title);
      formData.append("description", description);

      if (editingBanner) {
        await updateFooterAdBanner(editingBanner.id, formData);
        toast.success("Footer ad banner updated successfully");
      } else {
        await addFooterAdBanner(formData);
        toast.success("Footer ad banner added successfully");
      }
      toggle();
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || err?.message || `Failed to ${editingBanner ? "update" : "add"} footer ad banner`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this footer ad banner?")) return;
    try {
      await deleteFooterAdBanner(id);
      toast.success("Footer ad banner deleted");
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || "Failed to delete footer ad banner");
    }
  };

  const getImageUrl = (banner) => {
    if (!banner.image) return null;
    if (banner.image.startsWith("http")) return banner.image;
    const base = (API_BASE || "").replace("/api", "");
    return `${base}${banner.image.startsWith("/") ? "" : "/"}${banner.image}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Footer Ad" pageTitle="Website" />
        <Row>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Footer Ad Banners (Above Footer)</h5>
                <Button color="primary" onClick={toggle}>
                  <i className="ri-add-line align-bottom me-1"></i> Upload Footer Ad
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner /> Loading...
                </div>
              ) : banners.length === 0 ? (
                <p className="text-muted">No footer ad banners yet. Upload your first footer ad.</p>
              ) : (
                <Row>
                  {banners.map((b) => (
                    <Col md={6} lg={4} key={b.id} className="mb-3">
                      <div className="border rounded p-2 position-relative">
                        <img
                          src={getImageUrl(b)}
                          alt="Footer Ad"
                          className="img-fluid rounded"
                          style={{ maxHeight: 200, objectFit: "cover", width: "100%" }}
                        />
                        {b.link_url && (
                          <div className="mt-2 small text-muted">Link: {b.link_url}</div>
                        )}
                        {b.title && (
                          <div className="mt-2 fw-semibold text-dark">{b.title}</div>
                        )}
                        {b.description && (
                          <div className="small text-muted text-truncate" style={{ maxLines: 2 }}>{b.description}</div>
                        )}
                        <div className="small text-muted">Order: {b.sort_order}</div>
                        <div className="d-flex gap-2 mt-2">
                          <Button
                            color="info"
                            size="sm"
                            onClick={() => handleEditClick(b)}
                          >
                            <i className="ri-pencil-fill"></i> Edit
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDelete(b.id)}
                          >
                            <i className="ri-delete-bin-line"></i> Delete
                          </Button>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </CardBody>
          </Card>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>{editingBanner ? "Edit Footer Ad Banner" : "Upload Footer Ad Banner"}</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Label>Image {!editingBanner && <span className="text-danger">*</span>}</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files[0] || null)}
                required={!editingBanner}
              />
              {editingBanner && (
                <div className="mt-1 small text-muted">
                  Leave empty to keep the current image.
                </div>
              )}
            </div>
            <div className="mb-3">
              <Label>Title (optional)</Label>
              <Input
                type="text"
                placeholder="Enter title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Label>Description (optional)</Label>
              <Input
                type="textarea"
                rows="3"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Label>Link URL (optional)</Label>
              <Input
                type="url"
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggle}>Cancel</Button>
              <Button type="submit" color="primary" disabled={submitting}>
                {submitting ? (editingBanner ? "Saving..." : "Uploading...") : (editingBanner ? "Save Changes" : "Upload")}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default FooterAdBanner;
